import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { Accordion, Modal } from "react-bootstrap";
import Select from "react-select";
import defaultAvatar from "../../../../src/assets/images/placeholder.jpg";
import icon from "../../../../src/assets/images/share-link.png";
import Reshedule from "../../../../src/assets/images/deadline.png";
import backtoqueue from "../../../../src/assets/images/add_back_queue.png";
import completeIcon from "../../../../src/assets/images/check_11601272.png";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import { getDoctorsAction } from "../../../store/actions/DoctorsAction";
import {
  getAssignedAppointmentAction,
  getAvailableSlotsAction,
  confirmAppointmentAction,
  getConfirmedAction,
  removeAppointmentAction,
} from "../../../store/actions/AssignedAppointmentAction";
import { sendFormLinkAction, rescheduleAppointmentAction } from "../../../store/actions/ReceptionistAction";
import { formatDate } from "../../../utils/dateUtils";

// Utility function to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  
  // Get local date parts (not UTC)
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const AssignedAppointment = () => {
  const dispatch = useDispatch();
  
  // Refs to track component mount state
  const isMounted = useRef(true);
  
  // State for filters - initialize with today's date
  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(null);
  
  // State for time slots and selections
  const [selectedTimeSlots, setSelectedTimeSlots] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  
  // State for modals
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // State for reschedule form - initialize with today's date
  const [modalDate, setModalDate] = useState(getTodayDateString());
  const [modalBranch, setModalBranch] = useState(null);
  const [modalMode, setModalMode] = useState("Online");
  const [modalTimeSlot, setModalTimeSlot] = useState(null);
  
  // State for branch edit
  const [editBranch, setEditBranch] = useState(null);
  const [editAppointmentId, setEditAppointmentId] = useState(null);
  const [editAppointmentType, setEditAppointmentType] = useState(null); // 'assigned' or 'confirmed'
  
  // Loading states
  const [branchUpdateLoading, setBranchUpdateLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState({
    assigned: false,
    confirmed: false
  });
  
  // Individual action loading states
  const [confirmLoading, setConfirmLoading] = useState({});
  const [removeLoading, setRemoveLoading] = useState({});
  const [sendLinkLoading, setSendLinkLoading] = useState({});
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // Local state for appointments to show immediate updates
  const [localAssignedAppointments, setLocalAssignedAppointments] = useState([]);
  const [localConfirmedAppointments, setLocalConfirmedAppointments] = useState([]);

  // Redux selectors
  const doctors = useSelector((state) => state.doctors.doctors || []);
  const branches = useSelector((state) => state.branches?.branches || []);
  const loading = useSelector((state) => state.doctors.loading || false);
  
  const {
    assigned = [],
    confirmed = [],
    availableSlots = [],
    summary = {}
  } = useSelector((state) => state.assignedAppointments || {});

  // Initialize component
  useEffect(() => {
    isMounted.current = true;
    
    // Log the today's date for debugging
    console.log("Today's date set to:", getTodayDateString());

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load branches and doctors
  useEffect(() => {
    dispatch(getBranchesAction(false));
    dispatch(getDoctorsAction(false));
  }, [dispatch]);

  // Set default doctor if available

// Set default doctor from API data - dynamically selects first doctor
useEffect(() => {
  if (doctors && doctors.length > 0 && !selectedDoctorId) {
    const defaultDoctor = doctors[0]; // Get the first doctor from API response
    setSelectedDoctorId(defaultDoctor.doctor_id);
  }
}, [doctors, selectedDoctorId]);
  // Fetch assigned (booked) appointments
  const fetchAssignedAppointments = useCallback(async () => {
    if (!isMounted.current) return;
    
    setIsRefreshing(prev => ({ ...prev, assigned: true }));
    
    const payload = {
      pagination: true,
      pageId: 1,
      status: "Waiting",
      appointment_visited: "No",
      is_payment: "Yes",
      is_confirmed: "No",
      branch: selectedBranch?.value || "",
      mode: "",
      doctor_id: "0",
      date: selectedDate || getTodayDateString(),
      search_keyword: "",
      sort_by: "date",
      sort_order: "ASC",
    };

    try {
      await dispatch(getAssignedAppointmentAction(payload));
    } finally {
      if (isMounted.current) {
        setIsRefreshing(prev => ({ ...prev, assigned: false }));
      }
    }
  }, [dispatch, selectedBranch, selectedDate]);

  // Fetch confirmed appointments
  const fetchConfirmedAppointments = useCallback(async () => {
    if (!isMounted.current || !selectedDoctorId) return;
    
    setIsRefreshing(prev => ({ ...prev, confirmed: true }));
    
    const payload = {
      doctor_id: selectedDoctorId,
      pagination: true,
      pageId: 1,
      branch_id: selectedBranch?.value || "",
      mode: "",
      date: selectedDate || getTodayDateString(),
      status: "",
      search_keyword: "",
      sort_by: "",
      sort_order: "",
    };

    try {
      await dispatch(getConfirmedAction(payload));
    } finally {
      if (isMounted.current) {
        setIsRefreshing(prev => ({ ...prev, confirmed: false }));
      }
    }
  }, [dispatch, selectedDoctorId, selectedBranch, selectedDate]);

  // Update local state when Redux state changes
  useEffect(() => {
    // Transform and set local assigned appointments
    const transformedAssigned = assigned
      .filter((item) =>
        item?.appointment_id ||
        item?.patient_full_name ||
        item?.patient_name ||
        item?.phone_no
      )
      .map((item) => ({
        id: item.appointment_id,
        appointment_id: item.appointment_id,
        name: item.patient_full_name || item.patient_name || "",
        patient_id: item.patient_id,
        doctor: item.doctor_name || "",
        disease: item.diseases
          ? [...new Set(
              item.diseases
                .split(",")
                .map(d => d.trim())
                .filter(Boolean)
            )].join(", ")
          : "",
        timeslot: item.time_slot || "",
        date: item.date || "",
        branch: item.branch_name || "",
        branch_id: item.branch_id || "",
        avatar: item.avatar || null,
        phone_no: item.phone_no || "",
        email: item.email || "",
        mode: item.mode || "",
        status: item.appointment_status || "",
        appointment_camp_reg: item.appointment_camp_reg || "",
        is_firsttime_visitor: item.is_firsttime_visitor,
      }));
    
    setLocalAssignedAppointments(transformedAssigned);
  }, [assigned]);

  // Update local confirmed appointments when Redux state changes
  useEffect(() => {
    const transformedConfirmed = confirmed.map((timeSlotData) => {
      const [open_time, close_time] = (timeSlotData.time_slot || "").split("-");

      return {
        time: timeSlotData.time_slot,
        open_time,
        close_time,
        label: `${open_time || "N/A"} - ${close_time || "N/A"}`,
        opening_hour_id: timeSlotData.opening_hour_id,
        doctor_name: timeSlotData.doctor_name,
        confirmed_count: timeSlotData.confirmed_count,
        allowed_count: timeSlotData.allowed_count, 
        available_slots: timeSlotData.available_slots,
        date: timeSlotData.date,
        patients: (timeSlotData.patients || []).map((patient) => {
          // Find branch name from branches list using branch_id
          const branchObj = branches.find(b => String(b.branch_id) === String(patient.branch_id));
          
          // Extract queue details to check if patient is presented
          const isPresented = patient.queueDetails?.is_presented === "Yes";
          
          return {
            id: patient.appointment_id,
            appointment_id: patient.appointment_id,
            name: patient.patient_full_name || patient.patient_name,
            disease: patient.diseases || "-",
            doctor: timeSlotData.doctor_name,
            patient_id: patient.patient_id,
            timeslot: timeSlotData.time_slot,
            date: timeSlotData.date,
            branch: branchObj?.branch_name || "-",
            branch_id: patient.branch_id || "",
            avatar: patient.avatar || null,
            phone_no: patient.phone_no,
            email: patient.email,
            appointment_status: patient.appointment_status,
            mode: patient.mode,
            is_presented: isPresented, // Add is_presented flag
            queueDetails: patient.queueDetails
          };
        }),
      };
    });
    
    setLocalConfirmedAppointments(transformedConfirmed);
  }, [confirmed, branches]);

  // Fetch both lists
  const refreshBothLists = useCallback(async () => {
    await Promise.all([
      fetchAssignedAppointments(),
      fetchConfirmedAppointments()
    ]);
  }, [fetchAssignedAppointments, fetchConfirmedAppointments]);

  // Initial fetch and when filters change
  useEffect(() => {
    fetchAssignedAppointments();
    fetchConfirmedAppointments();
  }, [fetchAssignedAppointments, fetchConfirmedAppointments]);

  // Load available time slots when doctor or date changes
  useEffect(() => {
    if (!selectedDoctorId || !selectedDate) return;

    dispatch(
      getAvailableSlotsAction({
        doctor_id: selectedDoctorId,
        date: selectedDate,
      })
    );
  }, [selectedDoctorId, selectedDate, dispatch]);

  // Update time slots when availableSlots changes
  useEffect(() => {
    if (!availableSlots || availableSlots.length === 0) {
      setTimeSlots([]);
      return;
    }

    const mappedSlots = availableSlots.map((slot) => ({
      value: slot.time_slot,
      label: slot.time_slot,
      opening_hour_id: slot.opening_hour_id,
      available_slots: slot.available_slots,
      max_appointments: slot.max_appointments,
    }));

    setTimeSlots(mappedSlots);
  }, [availableSlots]);

  // Create doctor options
  const doctorOptions = doctors.map((doctor) => ({
    value: doctor.doctor_id,
    label: doctor.doctor_name,
    specialization: doctor.specialization,
  }));

  const selectedDoctorOption = doctorOptions.find((option) =>
    String(option.value) === String(selectedDoctorId)
  );

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  );

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "38px",
      fontSize: "14px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: "0px",
      fontSize: "14px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  // Event handlers
  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctorId(selectedOption ? selectedOption.value : "");
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleBranchFilterChange = (selectedOption) => {
    setSelectedBranch(selectedOption);
  };

  const handleTimeSlotChange = (selectedOption, patientId) => {
    setSelectedTimeSlots((prev) => ({
      ...prev,
      [patientId]: selectedOption,
    }));
  };

  const handleRemove = async (appointmentId, patientName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Remove appointment for ${patientName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setRemoveLoading(prev => ({ ...prev, [appointmentId]: true }));
      try {
        await dispatch(removeAppointmentAction(appointmentId));
        // Refresh both lists after removal
        await refreshBothLists();
        Swal.fire("Removed!", "Appointment has been removed.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to remove appointment", "error");
      } finally {
        setRemoveLoading(prev => ({ ...prev, [appointmentId]: false }));
      }
    }
  };

  const handleSendLink = async (patientId) => {
    setSendLinkLoading(prev => ({ ...prev, [patientId]: true }));
    try {
      await dispatch(sendFormLinkAction(patientId));
      // Swal.fire("Success", "Payment link sent successfully", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to send payment link", "error");
    } finally {
      setSendLinkLoading(prev => ({ ...prev, [patientId]: false }));
    }
  };

  const handleConfirmDoctorAssign = async (appointmentId, patientName, patientBranchId, isBranchEdit = false) => {
    const selectedSlot = selectedTimeSlots[appointmentId];

    // Validation: Check if a time slot is selected
    if (!selectedSlot) {
      Swal.fire(
        "Validation Error",
        "Please select a time slot first",
        "error"
      );
      return;
    }

    // Validation: Check if a doctor is selected in the filter
    if (!selectedDoctorId) {
      Swal.fire("Validation Error", "Please select a doctor first", "error");
      return;
    }

    // Show confirmation dialog with appropriate message
    const confirmMessage = isBranchEdit 
      ? `Update branch for ${patientName}?`
      : `Assign this slot to ${patientName}?`;

    const result = await Swal.fire({
      title: isBranchEdit ? "Confirm Branch Update" : "Confirm Doctor Assignment",
      text: confirmMessage,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setConfirmLoading(prev => ({ ...prev, [appointmentId]: true }));
      try {
        // Construct the confirmation payload with all required fields
        const confirmPayload = {
          appointment_id: appointmentId,           // The appointment being confirmed
          doctor_id: selectedDoctorId,              // The selected doctor from filter
          time_slot: selectedSlot.value,            // The selected time slot value
          doctor_opening_hour_id: selectedSlot.opening_hour_id, // Opening hour ID from the slot
          branch_id: isBranchEdit ? editBranch?.value : patientBranchId // Use editBranch if branch edit, otherwise use patient's branch
        };

        // Dispatch the confirmation action to the API
        await dispatch(confirmAppointmentAction(confirmPayload));
        
        // Clear the selected time slot for this patient from local state
        setSelectedTimeSlots((prev) => {
          const newState = { ...prev };
          delete newState[appointmentId];
          return newState;
        });
        
        // Close branch modal if it was open
        if (isBranchEdit) {
          setShowBranchModal(false);
          setEditBranch(null);
          setEditAppointmentId(null);
          setBranchUpdateLoading(false);
        }
        
        // Refresh both booked and confirmed appointment lists to show updated data
        await refreshBothLists();
        
        // Show success message to the user
        Swal.fire(
          "Success!", 
          isBranchEdit ? "Branch has been updated." : "Appointment has been confirmed.", 
          "success"
        );
      } catch (error) {
        // Handle any errors during the confirmation process
        Swal.fire("Error", "Failed to update appointment", "error");
      } finally {
        setConfirmLoading(prev => ({ ...prev, [appointmentId]: false }));
        if (isBranchEdit) {
          setBranchUpdateLoading(false);
        }
      }
    } else {
      if (isBranchEdit) {
        setBranchUpdateLoading(false);
      }
    }
  };

  const handleRescheduleWithApi = async () => {
    if (!modalDate || !modalBranch) {
      Swal.fire(
        "Validation Error",
        "Please fill all reschedule fields (Date, Branch)",
        "error"
      );
      return;
    }
  
    const payload = {
      appointment_id: Number(selectedAppointment?.appointment_id),
      date: modalDate,
      mode: modalMode,
      branch: modalBranch?.value || "",
    };
  
    setRescheduleLoading(true);
    try {
      const res = await dispatch(rescheduleAppointmentAction(payload));
  
      if (res?.status === "success") {
        Swal.fire("Success", res.message, "success");
        setShowInfoModal(false);
        setModalTimeSlot(null);
        
        // Refresh both lists after reschedule
        await refreshBothLists();
      } else {
        throw new Error(res?.message || "Failed to reschedule");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to reschedule", "error");
    } finally {
      setRescheduleLoading(false);
    }
  };

  // Function to update branch locally only - NO API CALL
  const handleLocalBranchUpdate = (appointmentId, newBranch, appointmentType) => {
    console.log("Local branch update:", { appointmentId, newBranch, appointmentType });
    
    if (appointmentType === 'assigned') {
      setLocalAssignedAppointments(prev => 
        prev.map(apt => 
          apt.id === appointmentId 
            ? { 
                ...apt, 
                branch: newBranch.label,
                branch_id: newBranch.value 
              } 
            : apt
        )
      );
      console.log("Updated assigned appointments");
    } else if (appointmentType === 'confirmed') {
      setLocalConfirmedAppointments(prev => 
        prev.map(slot => ({
          ...slot,
          patients: slot.patients.map(patient => 
            patient.id === appointmentId 
              ? { 
                  ...patient, 
                  branch: newBranch.label,
                  branch_id: newBranch.value 
                } 
              : patient
          )
        }))
      );
      console.log("Updated confirmed appointments");
    }
  };

  const handleOpenBranchModal = (patient, type) => {
    console.log("Opening branch modal for:", { patient, type });
    setSelectedAppointment(patient);
    setEditAppointmentId(patient.id);
    setEditAppointmentType(type);
    
    // Find the branch from branches array that matches the patient's branch
    const branchOption = branches.find(
      (b) => String(b.branch_name) === String(patient.branch)
    );
    
    setEditBranch(
      branchOption
        ? {
            value: branchOption.branch_id,
            label: branchOption.branch_name,
          }
        : null
    );
    
    setShowBranchModal(true);
  };

  const handleBranchModalConfirm = () => {
    if (!editBranch) {
      Swal.fire("Warning", "Please select a branch", "warning");
      return;
    }
    
    console.log("Branch modal confirm - type:", editAppointmentType);
    console.log("Appointment ID:", editAppointmentId);
    console.log("Selected branch:", editBranch);
    
    // Update locally immediately - NO API CALL
    handleLocalBranchUpdate(editAppointmentId, editBranch, editAppointmentType);
    
    // Close modal and show success
    setShowBranchModal(false);
    setEditBranch(null);
    setEditAppointmentId(null);
    setBranchUpdateLoading(false);
    
    Swal.fire({
      icon: "success",
      title: "Success!",
      text: `Branch has been updated to ${editBranch.label} for ${selectedAppointment?.name || 'patient'}.`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  return (
    <div>
      <style>
        {`
          .accordion-button {
            border-radius: 0 !important;
          }
          .accordion-button:not(.collapsed) {
            border-radius: 0 !important;
          }
          .cursor-pointer {
            cursor: pointer;
          }
          .refreshing {
            opacity: 0.7;
            pointer-events: none;
          }
          .branch-edit-icon {
            cursor: pointer;
            color: #28a745;
            font-size: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-left: 6px;
            transition: all 0.2s ease;
          }
          .branch-edit-icon:hover {
            color: #1e7e34;
            transform: scale(1.1);
          }
          .action-icon {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .action-icon .loader-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
          }
          .action-icon img, .action-icon span {
            cursor: pointer;
            transition: opacity 0.2s;
          }
          .action-icon.disabled {
            opacity: 0.5;
            pointer-events: none;
          }
          .reschedule-disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .reschedule-disabled img {
            cursor: not-allowed;
          }
          [data-tooltip] {
            position: relative;
            cursor: pointer;
          }
          [data-tooltip]:before {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 130%;
            left: 50%;
            transform: translateX(-50%);
            padding: 5px 10px;
            background-color: #333;
            color: #fff;
            border-radius: 4px;
            font-size: 12px;
            white-space: nowrap;
            display: none;
            z-index: 1000;
            pointer-events: none;
          }
          [data-tooltip]:hover:before {
            display: block;
          }
          [data-tooltip]:after {
            content: '';
            position: absolute;
            bottom: 120%;
            left: 50%;
            transform: translateX(-50%);
            border-width: 5px;
            border-style: solid;
            border-color: #333 transparent transparent transparent;
            display: none;
          }
          [data-tooltip]:hover:after {
            display: block;
          }
        `}
      </style>

      <div className="row">
        {/* Doctor Select */}
        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
          <Select
            value={selectedDoctorOption || null}
            onChange={handleDoctorChange}
            options={doctorOptions}
            formatOptionLabel={formatOptionLabel}
            placeholder="Select a doctor..."
            isClearable
            className="mb-3"
            classNamePrefix="react-select"
            isDisabled={loading || isRefreshing.assigned || isRefreshing.confirmed}
            styles={customStyles}
          />
        </div>

        {/* Date Select */}
        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3 position-relative">
          <input
            type="date"
            className="form-control form-control-sm"
            value={selectedDate}
            min={getTodayDateString()} 
            onChange={handleDateChange}
            disabled={isRefreshing.assigned || isRefreshing.confirmed}
          />
          <i
            className="fa fa-calendar position-absolute"
            style={{
              right: "30px",
              top: "35%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: "18px",
              zIndex: 1,
              color: "#555",
            }}
          />
        </div>

        {/* Branch Filter Select */}
        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-3">
          <Select
            value={selectedBranch}
            onChange={handleBranchFilterChange}
            options={branches.map((branch) => ({
              value: branch.branch_id || branch.value,
              label: branch.branch_name || branch.label,
            }))}
            placeholder="Filter by Branch"
            classNamePrefix="select"
            styles={customStyles}
            isClearable
            isDisabled={isRefreshing.assigned || isRefreshing.confirmed}
          />
        </div>
      </div>

      <div className="row">
        {/* LEFT SIDE - BOOKED (UNCONFIRMED) APPOINTMENTS */}
        <div className="col-xl-6 col-xxl-6 col-lg-6">
          <div className="card border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0" style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                  Booked
                  {isRefreshing.assigned && (
                    <span className="ms-2">
                      <i className="fa fa-spinner fa-spin text-primary"></i>
                    </span>
                  )}
                </h4>
              </div>

              <div className="pt-3">
                <div
                  id="DZ_W_Todo5"
                  className={`widget-media dz-scroll ${isRefreshing.assigned ? 'refreshing' : ''}`}
                  style={{ height: "600px", overflowY: "auto", overflowX: "hidden" }}
                >
                  {localAssignedAppointments.length > 0 ? (
                    <ul className="timeline p-0 m-0">
                      {localAssignedAppointments.map((patient) => (
                        <li key={patient.id}>
                          <div className="timeline-panel d-flex align-items-start">
                            <div className="me-3">
                              <Link to="#">
                                <img
                                  src={patient.avatar || defaultAvatar}
                                  width="50"
                                  className="rounded-circle"
                                  alt={patient.name}
                                />
                              </Link>
                            </div>

                            <div className="flex-grow-1">
                              <div className="row align-items-center g-2 mb-2 flex-wrap">
                                <div className="col-md-4" style={{ minWidth: 0 }}>
                                  {/* Name | Disease */}
                                  {([patient.name, patient.disease].filter(Boolean).length > 0) && (
                                    <h6
                                      className="mb-0 d-flex align-items-center"
                                      style={{
                                        fontSize: "18px",
                                        fontWeight: 600,
                                        gap: "8px",
                                      }}
                                    >
                                      {[patient.name, patient.disease]
                                        .filter(Boolean)
                                        .join(" | ")}
                                    </h6>
                                  )}

                                  {/* Mode | Status */}
                                  {([patient.mode, patient.status].filter(Boolean).length > 0) && (
                                    <small className="text-muted d-block">
                                      {[patient.mode, patient.status]
                                        .filter(Boolean)
                                        .join(" | ")}
                                    </small>
                                  )}

                                  {/* Date | Camp Reg | Visitor Type */}
                                  {(formatDate(patient.date) || patient.appointment_camp_reg || patient.is_firsttime_visitor) && (
                                    <small className="text-muted d-block">
                                      {[
                                        formatDate(patient.date),
                                        patient.appointment_camp_reg,
                                        patient.is_firsttime_visitor === "Yes"
                                          ? "New"
                                          : patient.is_firsttime_visitor === "No"
                                          ? "Old"
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(" | ")}
                                    </small>
                                  )}
                                  
                                  {/* Branch with Edit Icon */}
                                  <div className="d-flex align-items-center">
                                    <small className="text-muted">
                                      Branch: {patient.branch || "N/A"}
                                    </small>
                                    <span
                                      onClick={() => handleOpenBranchModal(patient, 'assigned')}
                                      className="branch-edit-icon"
                                      title="Edit Appointment Branch"
                                    >
                                      <i className="fa fa-pencil"></i>
                                    </span>
                                  </div>
                                </div>

                                <div className="col-md-4" style={{ minWidth: 0 }}>
                                  <Select
                                    options={timeSlots}
                                    value={selectedTimeSlots[patient.id] || null}
                                    onChange={(selectedOption) =>
                                      handleTimeSlotChange(selectedOption, patient.id)
                                    }
                                    placeholder="Time Slot"
                                    isClearable
                                    styles={customStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    isDisabled={isRefreshing.assigned || confirmLoading[patient.appointment_id]}
                                  />
                                </div>

                                <div className="col-md-4 d-flex justify-content-end" style={{ minWidth: "100px" }}>
                                  <div className="d-flex gap-3 align-items-center flex-shrink-0">
                                    <div className={`action-icon ${sendLinkLoading[patient.patient_id] ? 'disabled' : ''}`} style={{ position: 'relative' }}>
                                      <img
                                        src={icon}
                                        alt="Share Link"
                                        width="24"
                                        height="24"
                                        className="cursor-pointer"
                                        title="Share link"
                                        onClick={() => !sendLinkLoading[patient.patient_id] && handleSendLink(patient.patient_id)}
                                      />
                                      {sendLinkLoading[patient.patient_id] && (
                                        <div className="loader-overlay">
                                          <i className="fa fa-spinner fa-spin text-primary"></i>
                                        </div>
                                      )}
                                    </div>

                                    <div className={`action-icon ${confirmLoading[patient.appointment_id] ? 'disabled' : ''}`} style={{ position: 'relative' }}>
                                      <img
                                        src={completeIcon || "/placeholder.svg"}
                                        alt="Confirm"
                                        width="24"
                                        height="24"
                                        className="cursor-pointer"
                                        title="Confirm appointment"
                                        onClick={() => {
                                          if (confirmLoading[patient.appointment_id]) return;
                                          const selectedSlot = selectedTimeSlots[patient.id];
                                          if (!selectedSlot) {
                                            Swal.fire(
                                              "Warning",
                                              "Please select a time slot first",
                                              "warning"
                                            );
                                            return;
                                          }
                                          handleConfirmDoctorAssign(
                                            patient.appointment_id,
                                            patient.name,
                                            patient.branch_id, // Pass the branch_id to preserve it during confirmation
                                            false // Not a branch edit
                                          );
                                        }}
                                      />
                                      {confirmLoading[patient.appointment_id] && (
                                        <div className="loader-overlay">
                                          <i className="fa fa-spinner fa-spin text-primary"></i>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-5">
                      <small className="text-muted">No booked appointments</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - CONFIRMED APPOINTMENTS BY TIME SLOT */}
        <div className="col-xl-6 col-xxl-6 col-lg-6">
          <div className="card border-0 pb-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <div className="d-flex align-items-center">
                  <h4 className="mb-0" style={{ fontSize: "1.4rem", fontWeight: "600" }}>
                    Confirmed
                    {isRefreshing.confirmed && (
                      <span className="ms-2">
                        <i className="fa fa-spinner fa-spin text-primary"></i>
                      </span>
                    )}
                  </h4>
                  <span
                    className="px-2 py-1 rounded-pill bg-info text-white ms-2"
                    style={{ fontSize: "1rem", lineHeight: "normal" }}
                  >
                    {summary?.total_confirmed || 0} / {summary?.allowedcount || 0}
                  </span>
                </div>
              </div>

              <div className="pt-3">
                <div
                  id="DZ_W_Todo6"
                  className={`widget-media dz-scroll ${isRefreshing.confirmed ? 'refreshing' : ''}`}
                  style={{ height: "600px", overflowY: "auto" }}
                >
                  {localConfirmedAppointments.length > 0 ? (
                    <Accordion
                      className="accordion accordion-no-gutter aacordion header-bg"
                      defaultActiveKey="0"
                    >
                      {localConfirmedAppointments.map((slot, index) => (
                        <Accordion.Item key={index} eventKey={`${index}`}>
                          <Accordion.Header
                            as="div"
                            className="bg-info text-white"
                            style={{
                              borderRadius: "0px",
                              margin: 0,
                              boxShadow: "none",
                              border: "none",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span style={{ fontWeight: "600", fontSize: "14px" }}>
                                {slot.label}
                              </span>
                              <span
                                className="px-2 py-1 rounded-pill bg-white text-info"
                                style={{ fontSize: "12px", fontWeight: "600" }}
                              >
                                {slot.confirmed_count}/{slot.allowed_count}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Collapse eventKey={`${index}`}>
                            <div className="accordion-body p-0">
                              <div className="row g-0">
                                {slot.patients.length > 0 ? (
                                  slot.patients.map((patient, patientIndex) => (
                                    <div key={patientIndex} className="col-12 border-bottom">
                                      <div className="p-3">
                                        <div className="timeline-panel d-flex align-items-center">
                                          {/* Avatar */}
                                          <div className="me-3">
                                            <Link to="#">
                                              <img
                                                src={patient.avatar || defaultAvatar}
                                                alt={patient.name || "Patient"}
                                                width="45"
                                                className="rounded-circle"
                                              />
                                            </Link>
                                          </div>

                                          {/* Patient Info */}
                                          <div className="media-body flex-grow-1">
                                            {/* Patient Name */}
                                            {patient.name && (
                                              <h6
                                                className="mb-1"
                                                style={{ fontSize: "18px", fontWeight: 600 }}
                                              >
                                                {patient.name}
                                              </h6>
                                            )}

                                            {/* Mode | Status */}
                                            {[patient.mode, patient.appointment_status].some(Boolean) && (
                                              <small className="text-muted d-block">
                                                {[patient.mode, patient.appointment_status]
                                                  .filter(Boolean)
                                                  .join(" | ")}
                                              </small>
                                            )}

                                            {/* Phone | Date | Time Slot */}
                                            {[patient.phone_no, formatDate(patient.date), patient.timeslot].some(Boolean) && (
                                              <small className="text-muted d-block">
                                                {[patient.phone_no, formatDate(patient.date), patient.timeslot]
                                                  .filter(Boolean)
                                                  .join(" | ")}
                                              </small>
                                            )}

                                            {/* Branch Info with Edit Icon */}
                                            <div className="d-flex align-items-center">
                                              <small className="text-muted">
                                                Branch: {patient.branch || "N/A"}
                                              </small>
                                              <span
                                                onClick={() => handleOpenBranchModal(patient, 'confirmed')}
                                                className="branch-edit-icon"
                                                title="Edit Branch"
                                              >
                                                <i className="fa fa-pencil"></i>
                                              </span>
                                            </div>
                                          </div>
                                          
                                          <div className="d-flex gap-2 align-items-center flex-shrink-0">
                                            {/* Reschedule Icon - Disabled if patient is presented */}
                                            <div 
                                              className={`action-icon ${rescheduleLoading ? 'disabled' : ''} ${patient.is_presented ? 'reschedule-disabled' : ''}`} 
                                              style={{ position: 'relative' }}
                                             
                                            >
                                              <img
                                                src={Reshedule || "/placeholder.svg"}
                                                alt="Reschedule"
                                                width="24"
                                                height="24"
                                                className={!patient.is_presented ? "cursor-pointer" : ""}
                                                onClick={() => {
                                                  if (rescheduleLoading || patient.is_presented) return;
                                                  setSelectedAppointment(patient);
                                                  setModalDate(patient.date || selectedDate);
                                                  
                                                  // Set mode from patient data (ONLINE/OFFLINE)
                                                  setModalMode(patient.mode || "Online");
                                                  
                                                  // Set branch using branch_id
                                                  if (patient.branch_id) {
                                                    const branchOption = branches.find(b => 
                                                      String(b.branch_id) === String(patient.branch_id)
                                                    );
                                                    
                                                    if (branchOption) {
                                                      setModalBranch({
                                                        value: branchOption.branch_id,
                                                        label: branchOption.branch_name,
                                                      });
                                                    } else {
                                                      setModalBranch(null);
                                                    }
                                                  } else {
                                                    setModalBranch(null);
                                                  }
                                                  
                                                  setModalTimeSlot(
                                                    patient.timeslot
                                                      ? { label: patient.timeslot, value: patient.timeslot }
                                                      : null
                                                  );
                                                  setShowInfoModal(true);
                                                }}
                                                title={patient.is_presented ? "Patient is already in queue - Cannot reschedule" : "Reschedule Patient"}
                                              />
                                              {rescheduleLoading && selectedAppointment?.appointment_id === patient.appointment_id && (
                                                <div className="loader-overlay">
                                                  <i className="fa fa-spinner fa-spin text-primary"></i>
                                                </div>
                                              )}
                                            </div>

                                            {/* Share Link Icon */}
                                            <div className={`action-icon ${sendLinkLoading[patient.patient_id] ? 'disabled' : ''}`} style={{ position: 'relative' }}>
                                              <img
                                                src={icon}
                                                alt="Share Link"
                                                width="24"
                                                height="24"
                                                className="cursor-pointer me-1"
                                                onClick={() => !sendLinkLoading[patient.patient_id] && handleSendLink(patient.patient_id)}
                                                title="Share Link"
                                              />
                                              {sendLinkLoading[patient.patient_id] && (
                                                <div className="loader-overlay">
                                                  <i className="fa fa-spinner fa-spin text-primary"></i>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="col-12">
                                    <div className="p-3 text-center">
                                      <small className="text-muted">
                                        No appointments for this slot
                                      </small>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Accordion.Collapse>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-5">
                      <small className="text-muted">No confirmed appointments</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Branch Modal */}
      <Modal show={showBranchModal} onHide={() => setShowBranchModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Branch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Please select a branch from the dropdown below to update the appointment branch.</p>
          <div className="mb-3">
            <label className="form-label fw-bold">Select Branch</label>
            <Select
              value={editBranch}
              onChange={setEditBranch}
              options={branches.map((branch) => ({
                value: branch.branch_id || branch.value,
                label: branch.branch_name || branch.label,
              }))}
              placeholder="Select Branch"
              isClearable
              styles={customStyles}
              isDisabled={branchUpdateLoading}
            />
          </div>
          {selectedAppointment && (
            <div className="alert alert-info mt-2 p-2">
              <small>
                <strong>Current Branch:</strong> {selectedAppointment.branch || "N/A"}
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => setShowBranchModal(false)}
            disabled={branchUpdateLoading}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleBranchModalConfirm}
            disabled={branchUpdateLoading}
          >
            {branchUpdateLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              "Confirm"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Reschedule Modal */}
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reschedule Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={modalDate}
              min={getTodayDateString()}
              onChange={(e) => setModalDate(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Branch</label>
            <Select
              value={modalBranch}
              onChange={setModalBranch}
              options={branches.map((branch) => ({
                value: branch.branch_id || branch.value,
                label: branch.branch_name || branch.label,
              }))}
              placeholder="Select Branch"
              styles={customStyles}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Mode</label>
            <select
              className="form-select"
              value={modalMode}
              onChange={(e) => setModalMode(e.target.value)}
            >
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowInfoModal(false)}
            disabled={rescheduleLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRescheduleWithApi}
            disabled={rescheduleLoading}
          >
            {rescheduleLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Rescheduling...
              </>
            ) : (
              "Reschedule"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignedAppointment;